import {
  Button,
  Flex,
  GridItem,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { Get, Post } from "api";
import GamelightButton from "components/button";
import { NAVBAR_HEIGHT } from "components/navbar/NavbarAdmin";
import React, { useEffect, useState } from "react";

type AccountManager = {
  firstName: string;
  lastName: string;
  mail: string;
  id: number;
};

const AccountManager = () => {
  const toast = useToast();

  const [accountManagers, setAccountManagers] = useState<AccountManager[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCreating, setCreating] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPw, setShowPw] = useState(false);
  const [showConfirmationPw, setShowConfirmationPw] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const onCreateAccountManagerClick = async () => {
    if (password !== confirmPassword) {
      toast({
        title: "Password Mismatch",
        description:
          "Please ensure that the password and confirmation password match.",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      return;
    }

    try {
      setCreating(true);
      const result = await Post("/manage/account-managers", {
        firstName,
        lastName,
        mail: email,
        password,
      });

      if (result.err) {
        const errString = typeof result.err === "string" ? result.err : "";

        toast({
          title: "Error",
          description:
            "Account manager couldn't be created. " + errString
              ? errString.charAt(0).toUpperCase() + errString.slice(1)
              : result.err,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        return;
      }

      toast({
        title: "Account Manager Created",
        description: "Account Manager has been created successfully.",
        status: "success",
        duration: 9000,
        isClosable: true,
      });

      getAccountManagers();

      setFirstName("");
      setLastName("");
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setCreating(false);
    }
  };

  const getAccountManagers = async () => {
    try {
      setLoading(true);
      const result = (await Get(
        "/manage/account-managers"
      )) as AccountManager[];
      setAccountManagers(result);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAccountManagers();
  }, []);

  const textColor = useColorModeValue("secondaryGray.900", "white");

  return (
    <Flex
      flex={1}
      flexDir="column"
      minH={"90vh"}
      pt={{ base: "130px", md: "80px", xl: "80px" }}
    >
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <div />
        <GamelightButton onClick={onOpen}>
          Create New Account Manager
        </GamelightButton>
      </Flex>

      <Table
        variant="striped"
        color="gray.500"
        mb="24px"
        backgroundColor="white"
      >
        <Thead>
          <Tr>
            <Th>First Name</Th>
            <Th>Last Name</Th>
            <Th>Email</Th>
          </Tr>
        </Thead>
        <Tbody>
          {accountManagers.map((manager) => (
            <Tr key={manager.id}>
              <Td>
                <div>
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {manager.firstName}
                  </Text>
                </div>
              </Td>
              <Td>
                <div>
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {manager.lastName}
                  </Text>
                </div>
              </Td>
              <Td>
                <div>
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {manager.mail}
                  </Text>
                </div>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Account Manager</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid
              columns={1}
              gap={2}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onCreateAccountManagerClick();
                }
              }}
            >
              <Flex gap={2}>
                <Input
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                <Input
                  placeholder="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </Flex>
              <Input
                placeholder="Email"
                value={email}
                type="email"
                onChange={(e) => setEmail(e.target.value)}
              />
              <Flex gap={2}>
                <InputGroup>
                  <Input
                    placeholder="Password"
                    value={password}
                    type={showPw ? "text" : "password"}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <InputRightElement width="4.5rem">
                    <Button
                      h="1.75rem"
                      size="sm"
                      onClick={() => setShowPw((prev) => !prev)}
                    >
                      {showPw ? "Hide" : "Show"}
                    </Button>
                  </InputRightElement>
                </InputGroup>
                <InputGroup>
                  <Input
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    type={showConfirmationPw ? "text" : "password"}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <InputRightElement width="4.5rem">
                    <Button
                      h="1.75rem"
                      size="sm"
                      onClick={() => setShowConfirmationPw((prev) => !prev)}
                    >
                      {showConfirmationPw ? "Hide" : "Show"}
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </Flex>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter>
            <Button
              color="white"
              backgroundColor="brand.500"
              _hover={{ backgroundColor: "brand.600" }}
              onClick={onCreateAccountManagerClick}
              disabled={
                isCreating ||
                !firstName ||
                !lastName ||
                !email ||
                !password ||
                !confirmPassword
              }
              isLoading={isCreating}
              loadingText="Creating New Account..."
            >
              Create New Account
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default AccountManager;
